import { Divider, Grid, Button, Box, Typography, useTheme } from "@mui/material";
import MeasurementViewIntro from "./MeasurementViewIntro";
import { stopMeasurement } from "../../../api/project";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import UserProjectOverview from "../../Reuseables/UserProjectOverview";
import AreYouSureDialog from "../../Reuseables/Dialogs/AreYouSureDialog";
import ProjectCodeBox from "../../Reuseables/ProjectCodeBox";
import InfoDialog from "../../Reuseables/Dialogs/InfoDialog";
import { useApplicationContext } from "../../ContextProviders/ApplicationContext";
import { Project } from "../../../types/project";
import { StageView } from "../../../types/stageView";

import CreateUserAccountsPopup from "../../Reuseables/CreateUserAccountsPopup";
import { useOrganizationContext } from "../../ContextProviders/OrganizationContext";
import PostMeasurementDateDialog from "../../Reuseables/Dialogs/PostMeasurementDateDialog";
type MeasurementViewProps = {
  project: Project;
  setView: (s: StageView) => void;
};

export default function MeasurementView({
  project,
  setView,
}: MeasurementViewProps) {
  const [stopMeasurementDialog, setStopMeasurementDialog] = useState(false);
  const [areYouSureDialog, setAreYouSureDialog] = useState(false);
  const [postMDateDialog, setPostMDateDialog] = useState(false)
  const [createUserPopup, setCreateUserPopup] = useState(false);
  const [updatedProject, setUpdatedProject] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const { showSnackbar, showLoading, stopLoading } = useApplicationContext();
  const { updateProject } = useOrganizationContext();
  const handleDialogClose = () => {
    setStopMeasurementDialog(false);
    if (updatedProject) updateProject(updatedProject);
  };

  const theme = useTheme();
  const handlePostMDateDialogOpen = () => {
    setPostMDateDialog(true);
  }
  const handlePostMDateDialogClose = () => {
    setPostMDateDialog(false);
  }

  const handleCreateUserPopupClose = () => {
    setCreateUserPopup(false);
  };

  const handleDialogOpen = () => {
    setStopMeasurementDialog(true);
  };

  const handleAreYouSureClose = () => {
    setAreYouSureDialog(false);
  };
  const handleAreYouSureOpen = () => {
    setAreYouSureDialog(true);
  };

  const _stopMeasurement = async () => {
    setIsSubmitting(true);
    showLoading("loading.stopMeasurement");
    try {
      let result = await stopMeasurement(project);
      setIsSubmitting(false);
      if (result.projectType === 0) {
        setUpdatedProject({ ...result });
        handleDialogOpen();
      } else {
        updateProject(result);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        showSnackbar(error.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
      setIsSubmitting(false);
    } finally {
      stopLoading();
    }
  };
  return (
    <Grid
      container
      mt={2}
      sx={{
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
          lg: "row",
        },
      }}
    >
      <Grid item xs>
        <Grid container>
          <Grid item flexGrow={1} />
          <Grid item xs={12} sm={8} md={10} lg={10} xl={8}>
            <MeasurementViewIntro />
          </Grid>
          <Grid item flexGrow={1} />
        </Grid>
      </Grid>

      <Divider orientation="vertical" flexItem />
      <Grid item xs>
        <Grid container sx={{ marginTop: { xs: 4, sm: 4, md: 4, lg: 0 } }}>
          <Grid item flexGrow={1} />
          <Grid item xs={12} sm={8} md={10} lg={10} xl={8}>
            <ProjectCodeBox projectCode={project.projectCode} />
            <UserProjectOverview projectCode={project.projectCode} />

            <Box mt={3}>{t("introductions.measurement.text5")}</Box>
            <Box mt={3}>{t("introductions.measurement.text6")}</Box>
            <Grid container mt={1} flexDirection={"column"} spacing={3}>
              <Grid item xs>
                <Grid container width={"100%"} spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setCreateUserPopup(true)}
                    >
                      Generate user accounts
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{height: "100%"}}
                      onClick={() => {
                        setView({ stage: 8 });
                      }}
                    >
                      {t("buttons.seeDataAnalysis")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                <Grid container width={"100%"} spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Button fullWidth variant="contained" onClick={handlePostMDateDialogOpen}>
                      Add post-measurement start date
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Button fullWidth sx={{height: "100%"}} variant="contained" onClick={handleAreYouSureOpen}>
                      {t("buttons.stopMeasurement")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item flexGrow={1} />
        </Grid>
      </Grid>
      <InfoDialog
        color={"green"}
        title={t("dialogs.successHeader")}
        handleClose={handleDialogClose}
        isDialogOpen={stopMeasurementDialog}
        content={<Typography>{t("dialogs.stopMeasurementText3")}</Typography>}
      />
      <PostMeasurementDateDialog
        color={theme.palette.text.primary}
        title={t('post-measurement-start-date-header')}
        handleClose={handlePostMDateDialogClose}
        project={project}
        isDialogOpen={postMDateDialog}
        content={<Typography>{t('post-measurement-create-body')}</Typography>}
      />

      <AreYouSureDialog
        title={t("buttons.stopMeasurement")}
        content={<Typography>{t("dialogs.stopMeasurementText")}</Typography>}
        isDialogOpen={areYouSureDialog}
        handleClose={handleAreYouSureClose}
        onSubmit={_stopMeasurement}
        isSubmitting={isSubmitting}
      />
      <CreateUserAccountsPopup
        project={project}
        isDialogOpen={createUserPopup}
        handleClose={handleCreateUserPopupClose}
      />
    </Grid>
  );
}
